import { ElMessage, MessageHandler } from "element-plus";
import { ApiError } from "~/api";
import { authStore } from "~/stores/authStore";
import { AuthStoreType } from "~/types/auth";
import { getMessageTime } from "./miscUtils";

const displayedErrors: MessageHandler[] = [];

export const clearErrors = () => {
  displayedErrors.forEach((err) => err.close());
};
const userLogin = async () => {
  await authStore.login(AuthStoreType.User).catch((err) => {
    console.warn(err);
  });
};
const logErrorToDebug = (error: any): void => {
  const pdfDebugLogElem = document.getElementById("pdfDebugLog");
  if (pdfDebugLogElem) {
    pdfDebugLogElem.innerText += error.message + "\n";
  }
};

const handleValidationErrors = (errors: any): string => {
  const messages: string[] = [];
  for (const invalidModelErr of Object.values(errors)) {
    for (const err of invalidModelErr as any) {
      const msg = err as string;
      messages.push(msg);
      displayedErrors.push(
        ElMessage.error({
          message: msg,
          grouping: true,
          showClose: true,
          duration: 10 * getMessageTime(msg),
        }),
      );
    }
  }
  return messages.join("\n");
};

const handleUnauthorizedError = (): string => {
  const msg =
    'You must be logged in to access this resource. Click <a href="javascript:void(0)" id="login-link" style="color: #409EFF;">Login</a> to continue.';

  setTimeout(() => {
    const loginLink = document.getElementById("login-link");
    if (loginLink) {
      loginLink.addEventListener("click", userLogin);
    }
  }, 100);

  displayedErrors.push(
    ElMessage.error({
      message: msg,
      grouping: true,
      showClose: true,
      duration: 10 * getMessageTime(msg),
      dangerouslyUseHTMLString: true,
    }),
  );

  return "You must be logged in to access this resource.";
};

const showErrorMessage = (message: string, useHTML = false): string => {
  displayedErrors.push(
    ElMessage.error({
      message,
      grouping: true,
      showClose: true,
      duration: 10 * getMessageTime(message),
      ...(useHTML && { dangerouslyUseHTMLString: true }),
    }),
  );
  return message;
};

const handleApiError = (error: ApiError): string => {
  if (error.body?.errors) {
    return handleValidationErrors(error.body.errors);
  }

  if (error.message === "Unauthorized") {
    return handleUnauthorizedError();
  }

  const message =
    error.body?.title ||
    error.body?.message ||
    error.body?.detail ||
    error.body ||
    error.message ||
    error;

  return showErrorMessage(String(message));
};

const handleTypeError = (error: TypeError): string => {
  let msg = error.message;
  if (msg === "Failed to fetch") {
    msg =
      "Failed to connect to the server. Please check your internet connection.";
  }
  return showErrorMessage(msg);
};

/**
 * Displays an error to the user and returns the text that was shown
 * (or a plain-text equivalent when the UI message includes HTML).
 */
export function showError(error: any, rawError?: any): string {
  console.log("Displayed error:"); // keep, to assist in debugging
  console.log(error);
  if (rawError) {
    // for developers, the raw error may have more details than the displayed error, so log it too
    console.log("Raw error:");
    console.error(rawError);
  }

  logErrorToDebug(error);

  if (typeof error === "string") {
    return showErrorMessage(error);
  }

  if (error instanceof ApiError) {
    return handleApiError(error);
  }

  if (error instanceof TypeError) {
    return handleTypeError(error);
  }

  return showErrorMessage("Error: " + error.message);
}
